/**
 * Legacy single-trade snapshot — retired.
 *
 * This used to persist THE active trade to localStorage so a hard refresh could
 * show the position before the server round-trip landed. That was sound while
 * exactly one position could exist, because the charted market and the position's
 * market were by definition the same thing.
 *
 * With one position per coin it became actively harmful: the save path stamped
 * the CHARTED market's coin onto whatever position the store held, so switching
 * the chart to ETH while holding BTC wrote BTC's entry, size and margin under
 * `coin: 'ETH'`. The restore path then hydrated that as a real ETH position —
 * manufacturing a phantom position on every market the user visited, priced by
 * mixing one market's size with another's mark.
 *
 * Recovery is now `useSyncActiveTradesToStore`, which hydrates EVERY active trade
 * from `/speed/trades`. That is strictly more capable than a one-trade blob: it
 * covers all markets, it cannot disagree with the server, and it needs no coin
 * guessing because each row carries its own `pairBase`. The cost is that
 * positions appear one round-trip after a hard refresh rather than instantly.
 *
 * All that remains is the purge, so a stale blob written by an older build cannot
 * be read by anything (nothing reads it now) and does not sit in storage forever.
 */
import type { TradeStatus } from '@/features/wallet';

import type { CloseReason } from '../types';

/** Map backend terminal status to UI close reason. Unrelated to the retired
 *  snapshot — it just happens to have lived in this module. */
export const TERMINAL_STATUS_TO_CLOSE_REASON: Partial<
    Record<TradeStatus, CloseReason>
> = {
    Closed: 'manual',
    TakeProfit: 'TakeProfit',
    StopLoss: 'StopLoss',
    Liquidated: 'Liquidated',
    Failed: 'manual',
};

/** the key older builds wrote; kept only so the purge can find it. */
export const ACTIVE_TRADE_SNAPSHOT_KEY = 'inout.trade.active-snapshot.v1';

export function purgeLegacyTradeSnapshot(): void {
    try {
        localStorage.removeItem(ACTIVE_TRADE_SNAPSHOT_KEY);
    } catch {
        // storage unavailable (private mode, blocked cookies) — nothing to purge.
    }
}
